import React, { useEffect, useState } from "react";
import { AdminAchBankInfoCard } from "@/components/AdminAchBankInfoCard";
import PdfPreview from "@/components/PdfPreview";
import { Link, useNavigate, useParams } from "react-router-dom";
import { DashboardShell } from "@/components/DashboardShell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { depositToAmountInput } from "@/lib/deposit";
import { toast } from "sonner";
import { ChevronLeft, ChevronDown, ChevronRight, Send, FileText, FileSignature, X, Save, Download, RefreshCcw, AlertTriangle, ShieldCheck, FileWarning } from "lucide-react";
import { ContractSignaturePad } from "@/components/ContractSignaturePad";
import { useAuth } from "@/context/AuthContext";
import { buildRatePreview, formatRateChip } from "@/lib/rentalPricing";

const STATUS_LABEL = {
    draft: "Draft",
    sent_for_signature: "Sent for signature",
    customer_signed: "Customer signed",
    fully_signed: "Fully signed",
    cancelled: "Cancelled",
    imported: "Imported",
};

const STATUS_COLOR = {
    draft: "bg-slate-100 text-slate-700",
    sent_for_signature: "bg-blue-50 text-blue-900",
    customer_signed: "bg-amber-50 text-amber-900",
    fully_signed: "bg-emerald-50 text-emerald-800",
    cancelled: "bg-red-50 text-red-700",
    imported: "bg-violet-50 text-violet-800",
};

function fmt(iso) { if (!iso) return "—"; try { return new Date(iso).toLocaleString(); } catch { return iso; } }

export default function AdminContractDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [c, setC] = useState(null);
    const [templates, setTemplates] = useState([]);
    const [coiTemplates, setCoiTemplates] = useState([]);
    const [coiTemplateId, setCoiTemplateId] = useState("");
    // Phase H.14b — ACH template state. The list is filtered to non-archived
    // `template_type === "ach_authorization"` templates; the selection is
    // initialised from the contract's `ach_template_id` (which `_contract_to_public`
    // derives from documents[] for legacy contracts).
    const [achTemplates, setAchTemplates] = useState([]);
    const [achTemplateId, setAchTemplateId] = useState("");
    const [title, setTitle] = useState("");
    const [templateId, setTemplateId] = useState("");
    const [notes, setNotes] = useState("");
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [showCancel, setShowCancel] = useState(false);
    const [cancelReason, setCancelReason] = useState("");
    // Task 24 — Reassign is the default cancellation path; admin can opt
    // out of customer-facing reason visibility.
    const [cancelAction, setCancelAction] = useState("reassign");
    const [cancelShowToCustomer, setCancelShowToCustomer] = useState(true);
    const [showCountersign, setShowCountersign] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);
    // Phase Task 5 — bundled-preview sections metadata (Contract / ACH / COI).
    const [previewSections, setPreviewSections] = useState(null);
    const [rental, setRental] = useState(null);
    const [showRental, setShowRental] = useState(false);
    const [rentalForm, setRentalForm] = useState({ equipment_type: "", unit_number: "", make: "", model: "", year: "", vin: "", plate: "", start_date: "", end_date: "", daily_rate: "", monthly_rate: "", minimum_term_months: "", billing_period: "", deposit: "" });
    // Task 17 — tenant default deposits map (equipment_type → cents) used to
    // power the soft-warning at rental edit and the hard-confirm at send.
    const [defaultDeposits, setDefaultDeposits] = useState({});
    // Task 17 — Hard-confirm modal state for "send with $0 deposit".
    const [showZeroDepositModal, setShowZeroDepositModal] = useState(false);
    const [zeroDepositConfirmText, setZeroDepositConfirmText] = useState("");
    const [zeroDepositReason, setZeroDepositReason] = useState("");
    // Phase E1 — audit trail + integrity state
    const [events, setEvents] = useState([]);
    const [tampered, setTampered] = useState(false);
    const [pdfSha, setPdfSha] = useState(null);
    // Phase IMPORT-PAPER-REPLACE (2026-07-09) — replace imported paper contract file
    const [showReplacePaper, setShowReplacePaper] = useState(false);
    const [replacePaperFile, setReplacePaperFile] = useState(null);
    // Phase E2 — hash chain + RFC 3161 trusted timestamp state
    const [chain, setChain] = useState(null);
    const [rfc3161, setRfc3161] = useState(null);
    const [documentsTrust, setDocumentsTrust] = useState([]);
    const [verifyingChain, setVerifyingChain] = useState(false);
    const [verifyingTs, setVerifyingTs] = useState(false);
    const [retryingTs, setRetryingTs] = useState(false);

    // Bug 2 — Settings card collapses to a chevron once the contract is
    // fully signed (fields are immutable at that point so they just add
    // clutter). For not-yet-signed contracts we keep the legacy
    // always-expanded behavior. ``settingsExpanded === null`` means
    // "use the default for the current status"; user toggle pins it.
    const [settingsExpanded, setSettingsExpanded] = useState(null);
    // Bug 3 — ESIGN/UETA audit trail collapses by default for ALL
    // contracts. The header still shows event count + integrity pills
    // (compact view) so admins can verify at a glance without
    // expanding. ``null`` means "use default false"; explicit boolean
    // wins once toggled.
    const [auditExpanded, setAuditExpanded] = useState(null);

    // Bug 4 — "Send for signature" review modal. Opens BEFORE the API
    // fires so the admin can sanity-check the rental snapshot + flag
    // any missing/blank fields that would otherwise lock onto the
    // contract on send. ``customer`` is a lazy-load (fetched the first
    // time the modal opens) — the snapshot needs the company name +
    // contact email but the rest of the page never references it.
    const [showSendConfirm, setShowSendConfirm] = useState(false);
    const [sendError, setSendError] = useState(null);
    const [customer, setCustomer] = useState(null);

    const reload = async () => {
        setLoading(true);
        try {
            // === Phase 1: render-blocking minimum ===
            // Five endpoints needed for the page to be USEFUL on first
            // paint: the contract itself + the dropdowns the admin
            // might immediately interact with. All in parallel; max
            // wall-time = single slowest call.
            const [contract, tpls, coiTpls, achTpls, depDefaults] = await Promise.all([
                api.get(`/admin/contracts/${id}`),
                // Task 27 Bug-3 — narrow to contract-type templates only.
                api.get("/admin/templates", { params: { template_type: "contract", include_archived: false, limit: 200 } }),
                api.get("/admin/coi-requirements-templates"),
                // Phase H.14b — non-archived ACH templates for the new dropdown.
                api.get("/admin/templates", { params: { template_type: "ach_authorization", archived: false, limit: 200 } }),
                // Task 17 — default deposits map for soft-warning + hard-confirm.
                api.get("/admin/settings/default-deposits").catch(() => ({ data: { defaults: {} } })),
            ]);
            setC(contract.data);
            setTitle(contract.data.title || "");
            setTemplateId(contract.data.template_id || "");
            setCoiTemplateId(contract.data.coi_requirements_template_id || "");
            setAchTemplateId(contract.data.ach_template_id || "");
            setNotes(contract.data.notes || "");
            setTemplates(tpls.data?.items || []);
            setCoiTemplates(coiTpls.data?.items || []);
            setAchTemplates(achTpls.data?.items || []);
            setDefaultDeposits(depDefaults.data?.defaults || {});

            // === Phase 2: deferred — fire-and-forget in parallel ===
            // Task 29 — these were the perf killer. They were a chain
            // of 5 sequential awaits inside the loading state, which
            // on prod Atlas added 5-15s to time-to-first-paint. Now
            // we drop the loading spinner as soon as Phase 1 returns
            // and let these stream in as they land. Each `setX` is
            // independent so React batches naturally.
            const contractData = contract.data;
            setLoading(false);

            // Rental: only attached contracts have one.
            if (contractData.rental_id) {
                api.get(`/admin/rentals/${contractData.rental_id}`)
                    .then((rt) => {
                        setRental(rt.data);
                        // Task 33 Bug 1 — auto-prefill the Title input when
                        // the contract was created without one (legacy rows
                        // or programmatic callers). Backend now defaults
                        // this on new contracts, but a one-shot prefill on
                        // load covers the existing pool. The setter is
                        // guarded against overwriting any admin edit by
                        // checking the live state via the functional form.
                        //
                        // 2026-06-23 fix — also treat the legacy
                        // ``"Rental contract (pending template)"`` placeholder
                        // (and the related "pending template" string) as
                        // "empty" so contracts created by the equipment-assign
                        // path BEFORE Task 33 Bug 1's backend fix get
                        // heal-on-read prefill. The functional-setter guard
                        // still protects manually-edited titles.
                        const PLACEHOLDER_TITLES = [
                            "Rental contract (pending template)",
                            "Rental Agreement",
                            "ACH Authorization",
                        ];
                        const currentTitle = (contractData.title || "").trim();
                        const isPlaceholder = !currentTitle || PLACEHOLDER_TITLES.includes(currentTitle);
                        if (isPlaceholder) {
                            const eq = (rt.data.equipment_type || "").trim().replace(/_/g, " ").toUpperCase();
                            const unit = (rt.data.unit_number || "").trim();
                            let prefill = "";
                            if (eq && unit) prefill = `${eq} · #${unit}`;
                            else if (eq) prefill = eq;
                            else {
                                prefill = [rt.data.year, rt.data.make, rt.data.model]
                                    .filter(Boolean).join(" ").trim().slice(0, 64);
                            }
                            // Only overwrite when (a) prefill yielded a value
                            // AND (b) the live state is still empty or holds
                            // one of the known placeholder strings. Manual
                            // edits to anything else are preserved.
                            if (prefill) {
                                setTitle((cur) => {
                                    const c2 = (cur || "").trim();
                                    if (!c2 || PLACEHOLDER_TITLES.includes(c2)) return prefill;
                                    return cur;
                                });
                            }
                        }
                        setRentalForm({
                            equipment_type: rt.data.equipment_type || "",
                            unit_number: rt.data.unit_number || "", make: rt.data.make || "", model: rt.data.model || "",
                            year: rt.data.year || "", vin: rt.data.vin || "", plate: rt.data.plate || "",
                            start_date: rt.data.start_date || "", end_date: rt.data.end_date || "",
                            daily_rate: rt.data.daily_rate ?? "", monthly_rate: rt.data.monthly_rate ?? "",
                            minimum_term_months: rt.data.minimum_term_months ?? "",
                            billing_period: rt.data.billing_period || "",
                            // Phase DP polish 3 — coerce dict / legacy float into
                            // a plain dollar string the number input can render.
                            deposit: depositToAmountInput(rt.data.deposit),
                        });
                    })
                    .catch(() => { /* may have been cancelled */ });
            } else {
                setRental(null);
            }

            // PDF preview blob — the heaviest single call. Streams in
            // after first paint; the iframe shows a loading placeholder
            // until the URL is set.
            api.get(`/admin/contracts/${id}/pdf`, { responseType: "blob" })
                .then((pdfResp) => {
                    const blob = new Blob([pdfResp.data], { type: "application/pdf" });
                    const url = URL.createObjectURL(blob);
                    setPreviewUrl((old) => { if (old) URL.revokeObjectURL(old); return url; });
                })
                .catch(() => { /* ignore */ });

            // Phase E1 — events.
            api.get(`/admin/contracts/${id}/events`)
                .then((ev) => setEvents(ev.data?.events || []))
                .catch(() => { /* endpoint added in Phase E1 */ });

            // Phase E2 — chain + rfc3161 status.
            api.get(`/admin/contracts/${id}/audit-trail.json`)
                .then((at) => {
                    setChain(at.data?.chain || null);
                    // Task 32 follow-up Bug A — for bundled contracts the
                    // timestamp lives inside documents[i].rfc3161_* (Bug 2
                    // fix). The top-level rfc3161 block stays "none" because
                    // there is no top-level signed PDF. Fall back to the
                    // first per-doc trust entry that carries a token so the
                    // top-level pill mirrors what the user just retried,
                    // instead of confusingly showing "none retry" right
                    // after the success toast.
                    const topRfc = at.data?.rfc3161 || null;
                    const docsTrust = at.data?.documents_trust || [];
                    const topHas = topRfc && topRfc.token_present;
                    let effectiveRfc = topRfc;
                    if (!topHas) {
                        const docWithTs = docsTrust.find((d) => d.rfc3161?.token_present);
                        if (docWithTs) effectiveRfc = docWithTs.rfc3161;
                    }
                    setRfc3161(effectiveRfc);
                    setDocumentsTrust(docsTrust);
                })
                .catch(() => { /* ignore */ });

            // Signed-PDF integrity check — only when the contract is
            // fully_signed (the only state where a tamper indicator
            // can render).
            if (contractData.status === "fully_signed") {
                api.get(`/admin/contracts/${id}/pdf`, { params: { variant: "signed" }, responseType: "blob" })
                    .then((r) => {
                        setTampered(r.headers?.["x-rentflow-integrity"] === "tampered");
                        setPdfSha(r.headers?.["x-rentflow-pdf-sha256"] || contractData.signed_pdf_sha256 || null);
                    })
                    .catch(() => { /* ignore */ });
            } else {
                setTampered(false);
                setPdfSha(null);
            }
        } catch (err) {
            toast.error(formatApiError(err));
            setLoading(false);
        }
    };

    useEffect(() => { reload(); /* eslint-disable-next-line */ }, [id]);

    const isDraft = c?.status === "draft";
    const canSend = c?.status === "draft" || c?.status === "sent_for_signature";
    // Phase M — ACH-only contracts skip the countersign step entirely.
    const isPureAch = c?.type === "ach_authorization";
    const canCountersign = c?.status === "customer_signed" && !isPureAch;
    const canCancel = c && !["cancelled", "fully_signed"].includes(c.status);

    const onSavePatch = async () => {
        setBusy(true);
        try {
            const { data } = await api.patch(`/admin/contracts/${id}`, {
                title,
                template_id: templateId || null,
                notes,
                coi_requirements_template_id: coiTemplateId || null,
                // Phase H.14b — empty string clears the ACH bundle server-side.
                ach_template_id: achTemplateId || "",
            });
            setC(data);
            // The PATCH response is authoritative; resync local form state so
            // ACH dropdown reflects the canonical value (after backend
            // documents[] bookkeeping).
            setAchTemplateId(data.ach_template_id || "");
            toast.success("Saved");
        } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
    };

    const onSaveRental = async () => {
        setBusy(true);
        try {
            const payload = { ...rentalForm };
            ["year", "daily_rate", "monthly_rate", "deposit", "minimum_term_months"].forEach(k => {
                if (payload[k] === "" || payload[k] == null) delete payload[k];
                else payload[k] = Number(payload[k]);
            });
            if (!payload.billing_period) delete payload.billing_period;
            await api.post(`/admin/contracts/${id}/rental`, payload);
            toast.success("Rental saved");
            setShowRental(false);
            reload();
        } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
    };

    const onCopyLink = async () => {
        try {
            const url = `${window.location.origin}/sign/contract/${c.signing_token}`;
            await navigator.clipboard.writeText(url);
            toast.success("Signing link copied");
        } catch { toast.error("Copy failed"); }
    };

    const onResend = async () => {
        setBusy(true);
        try {
            await api.post(`/admin/contracts/${id}/resend`);
            toast.success("Reminder sent + new link generated");
            reload();
        } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
    };

    const onGeneratePreview = async () => {
        setBusy(true);
        try {
            // Phase Task 5 — capture sections metadata for the bundled-preview chip row.
            const resp = await api.post(`/admin/contracts/${id}/generate-pdf`);
            setPreviewSections(resp.data?.sections || null);
            setPreviewUrl(`/api/admin/contracts/${id}/pdf?ts=${Date.now()}`);
            const included = (resp.data?.sections || []).filter((s) => s.included).length;
            toast.success(`Preview regenerated — ${included} document${included === 1 ? "" : "s"} bundled`);
            reload();
        } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
    };

    const onSend = async (waiver = null) => {
        // Task 17 — Hard-confirm gate. When the linked rental has no
        // deposit on file, intercept the send and surface a modal that
        // requires the admin to type CONFIRM + a reason ≥ 8 chars before
        // we POST the waiver block to the backend.
        if (!waiver && rental && rental.deposit_warning?.applies) {
            setZeroDepositConfirmText("");
            setZeroDepositReason("");
            setShowZeroDepositModal(true);
            return;
        }
        // Bug 4 — Send-for-signature review modal. Opens BEFORE the API
        // call so the admin can review the rental snapshot. The waiver
        // branch above runs FIRST because a $0-deposit waiver is a
        // hard-gate (typed-confirmation contract), not a soft review.
        if (!waiver) {
            // Lazy-load the customer for company name + contact email
            // on the first open; subsequent opens reuse the cached doc.
            if (!customer && c?.customer_id) {
                api.get(`/admin/customers/${c.customer_id}`)
                    .then((r) => setCustomer(r.data))
                    .catch(() => { /* non-fatal; modal renders with id-only */ });
            }
            setSendError(null);
            setShowSendConfirm(true);
            return;
        }
        // Waiver branch fires the API directly — the zero-deposit modal
        // already gathered + showed the snapshot+attestation.
        return doSend(waiver);
    };

    // Phase IMPORT-PAPER-REPLACE — swap the paper contract file. Only
    // available on imported contracts. Backend endpoint archives the
    // previous file (GridFS ``superseded_at`` marker) so the audit
    // trail retains lineage.
    const onReplacePaperContract = async () => {
        if (!replacePaperFile || !c?.rental_id) return;
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("paper_contract", replacePaperFile);
            await api.post(
                `/admin/rentals/${c.rental_id}/paper-contract/replace`,
                fd,
                { headers: { "Content-Type": "multipart/form-data" } },
            );
            toast.success("Paper contract replaced. Previous file archived.");
            setShowReplacePaper(false);
            setReplacePaperFile(null);
            reload();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const doSend = async (waiver = null) => {
        setBusy(true);
        setSendError(null);
        try {
            const body = waiver ? { deposit_waiver: waiver } : {};
            await api.post(`/admin/contracts/${id}/send-for-signature`, body);
            const msg = waiver
                ? "Contract sent. Zero-deposit waiver logged."
                : "Signing link sent (mock email).";
            toast.success(msg);
            setShowZeroDepositModal(false);
            setShowSendConfirm(false);
            reload();        } catch (err) {
            // Backend can still return 409 deposit_waiver_required if the
            // rental wasn't reloaded since the deposit was cleared elsewhere.
            const detail = err?.response?.data?.detail;
            if (err?.response?.status === 409 && detail?.code === "deposit_waiver_required") {
                setZeroDepositConfirmText("");
                setZeroDepositReason("");
                setShowSendConfirm(false);
                setShowZeroDepositModal(true);
                toast.message("Confirm zero-deposit before sending.");
            } else {
                // Phase N.17.3 — TEMPLATE_REQUIRED gets an actionable hint.
                const msg = err?.response?.status === 409 && detail?.code === "TEMPLATE_REQUIRED"
                    ? `${detail.message}. Choose the ${(detail.needs_fields || []).includes("ach_template_id") ? "ACH Authorization" : "Rental Agreement"} template on this contract, then send again.`
                    : formatApiError(err);
                // Modal open → render inline + keep it open; toast for screen readers.
                if (showSendConfirm) setSendError(msg);
                toast.error(msg);
            }
        } finally { setBusy(false); }
    };

    const onCancel = async () => {
        // Task 24 — backend requires reason ≥8 chars now (was 3).
        const r = cancelReason.trim();
        if (r.length < 8) { toast.error("Reason must be at least 8 characters"); return; }
        setBusy(true);
        try {
            const resp = await api.post(`/admin/contracts/${id}/cancel`, {
                reason: r,
                action: cancelAction,
                show_reason_to_customer: cancelShowToCustomer,
            });
            const pathLabel = cancelAction === "reassign"
                ? "Reassign path — submission queued for new equipment"
                : "Archive path — submission marked cancelled";
            toast.success(`Contract cancelled. ${pathLabel}.`);
            setShowCancel(false);
            // Reset modal state so the next open is clean.
            setCancelReason("");
            setCancelAction("reassign");
            setCancelShowToCustomer(true);
            reload();
            return resp;
        } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
    };

    const onCountersign = async (payload) => {
        setBusy(true);
        try {
            await api.post(`/admin/contracts/${id}/countersign`, payload);
            toast.success("Contract fully signed");
            setShowCountersign(false);
            reload();
        } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
    };

    if (loading || !c) return <DashboardShell><p className="text-sm text-slate-500">Loading…</p></DashboardShell>;

    const sigCustomer = (c.signatures || []).find((s) => s.signer_role === "customer");
    const sigAdmin = (c.signatures || []).find((s) => s.signer_role === "admin");

    return (
        <DashboardShell>
            {tampered && (
                <div className="fade-up rounded-md border border-red-300 bg-red-50 p-4 mb-3 flex items-start gap-3" data-testid="contract-integrity-banner">
                    <FileWarning size={20} className="shrink-0 mt-0.5 text-red-700" />
                    <div className="text-sm text-red-900">
                        <p className="font-semibold">Integrity warning: the signed PDF on disk no longer matches its recorded SHA-256 hash.</p>
                        <p className="mt-1">Investigate immediately. The recorded hash and a tamper event have been preserved in the audit trail.</p>
                    </div>
                </div>
            )}
            <div className="fade-up flex items-start justify-between flex-wrap gap-3">
                <div className="space-y-1.5">
                    <Link to={`/admin/customers/${c.customer_id}`}
                        className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-blue-900"
                        data-testid="contract-back-customer"><ChevronLeft size={14} /> Back to customer</Link>
                    <p className="text-xs font-medium tracking-[0.18em] uppercase text-blue-900">CONTRACT · {c.id.slice(0, 8)}</p>
                    <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900" data-testid="contract-title">{c.title}</h1>
                    <div className="flex items-center gap-2 flex-wrap mt-2">
                        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_COLOR[c.status]}`} data-testid="contract-status">
                            {STATUS_LABEL[c.status] || c.status}
                        </span>
                        <span className="text-xs text-slate-500">
                            {c.type === "rental_agreement" ? "Rental Agreement" : "ACH Authorization"} · created {fmt(c.created_at)}
                        </span>
                    </div>
                </div>
                <div className="flex flex-wrap gap-2">
                    {!c.imported && (
                        <Button variant="outline" size="sm" onClick={onGeneratePreview} disabled={busy} className="gap-1.5"
                            data-testid="contract-generate-preview"><RefreshCcw size={14} /> Regenerate preview</Button>
                    )}
                    {c.imported && c.has_signed_pdf && (
                        <>
                        <Button variant="outline" size="sm" className="gap-1.5"
                            data-testid="contract-download-paper"
                            onClick={async () => {
                                try {
                                    const resp = await api.get(`/admin/contracts/${id}/pdf`, { responseType: "blob" });
                                    const blob = new Blob([resp.data], { type: "application/pdf" });
                                    const url = URL.createObjectURL(blob);
                                    const a = document.createElement("a");
                                    a.href = url; a.download = `${c.title}.pdf`; a.click();
                                    URL.revokeObjectURL(url);
                                } catch (err) { toast.error(formatApiError(err)); }
                            }}>
                            <Download size={14} /> Download paper contract
                        </Button>
                        <Button variant="outline" size="sm" className="gap-1.5"
                            data-testid="contract-replace-paper-open"
                            onClick={() => { setReplacePaperFile(null); setShowReplacePaper(true); }}>
                            <RefreshCcw size={14} /> Replace file
                        </Button>
                        </>
                    )}
                    {c.status === "sent_for_signature" && (
                        <>
                            <Button variant="outline" size="sm" onClick={onCopyLink} className="gap-1.5"
                                data-testid="contract-copy-link"><FileText size={14} /> Copy signing link</Button>
                            <Button variant="outline" size="sm" onClick={onResend} disabled={busy} className="gap-1.5"
                                data-testid="contract-resend"><Send size={14} /> Resend</Button>
                        </>
                    )}
                    {canSend && c.status === "draft" && (
                        <Button onClick={() => onSend()} disabled={busy} className="bg-blue-900 hover:bg-blue-800 gap-1.5"
                            data-testid="contract-send"><Send size={14} /> Send for signature</Button>
                    )}
                    {canCountersign && (
                        <Button onClick={() => setShowCountersign(true)} disabled={busy} className="bg-emerald-700 hover:bg-emerald-800 gap-1.5"
                            data-testid="contract-countersign"><FileSignature size={14} /> Countersign</Button>
                    )}
                    {canCancel && (
                        <Button variant="outline" size="sm" onClick={() => setShowCancel(true)} className="border-red-200 text-red-700 hover:bg-red-50 gap-1.5"
                            data-testid="contract-cancel"><X size={14} /> Cancel</Button>
                    )}
                </div>
            </div>

            <div className="mt-8 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6 items-start">
                <Card>
                    <CardContent className="p-0">
                        {c.imported && (
                            <div className="rounded-t-md border-b border-violet-200 bg-violet-50 p-3 flex items-start gap-2"
                                data-testid="contract-imported-banner">
                                <AlertTriangle size={16} className="shrink-0 mt-0.5 text-violet-700" />
                                <div className="text-xs text-violet-900">
                                    <strong>Imported contract.</strong> The PDF below is the original paper contract — digital signing is not available for imported records.
                                </div>
                            </div>
                        )}
                        <PdfPreview
                            src={previewUrl}
                            downloadHref={previewUrl}
                            downloadName={c.title ? `${c.title}.pdf` : "contract.pdf"}
                            testid="contract-pdf-frame"
                        />
                        {/* Phase Task 5 — bundled-sections chip row */}
                        {previewSections && previewSections.length > 0 && (
                            <div
                                className="px-4 py-3 border-t border-slate-200 bg-slate-50 flex items-center gap-2 flex-wrap"
                                data-testid="contract-preview-sections"
                            >
                                <span className="text-xs font-medium text-slate-600 mr-1">Bundled:</span>
                                {previewSections.map((s) => {
                                    const label =
                                        s.type === "rental_agreement" ? "Contract"
                                            : s.type === "ach_authorization" ? "ACH Authorization"
                                                : s.type === "coi_request" ? "COI Requirements"
                                                    : s.type;
                                    const reasonLabel = s.reason === "not_bundled"
                                        ? "not bundled"
                                        : s.reason === "not_required"
                                            ? "not required"
                                            : s.reason === "render_failed"
                                                ? "render failed"
                                                : "";
                                    return (
                                        <span
                                            key={s.type}
                                            data-testid={`contract-preview-section-${s.type}`}
                                            title={s.included ? `${s.page_count} page${s.page_count === 1 ? "" : "s"}` : `(${reasonLabel})`}
                                            className={
                                                "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium " +
                                                (s.included
                                                    ? "bg-emerald-50 text-emerald-900 ring-1 ring-emerald-200"
                                                    : "bg-slate-100 text-slate-500 ring-1 ring-slate-200")
                                            }
                                        >
                                            {s.included ? "✓ " : "— "}{label}
                                            {s.included
                                                ? <span className="text-emerald-700 font-normal ml-0.5">({s.page_count}p)</span>
                                                : <span className="text-slate-400 font-normal ml-0.5">({reasonLabel})</span>}
                                        </span>
                                    );
                                })}
                            </div>
                        )}
                    </CardContent>
                </Card>

                <div className="space-y-4">
                    {(c.documents || []).filter((d) => d.doc_type === "ach_authorization" && d.ach_bank_info).map((d) => (
                        <AdminAchBankInfoCard key={d.doc_id} contractId={c.id} doc={d} />
                    ))}

                    {c.type === "rental_agreement" && (
                        <Card>
                            <CardContent className="p-5 space-y-3" data-testid="contract-rental-panel">
                                <div className="flex items-start justify-between gap-2">
                                    <p className="text-xs font-medium tracking-[0.18em] uppercase text-slate-500">Rental</p>
                                    {!rental && (
                                        <Button size="sm" variant="outline" onClick={() => setShowRental(true)} className="gap-1.5"
                                            data-testid="contract-attach-rental">+ Attach</Button>
                                    )}
                                </div>
                                {rental ? (
                                    <div className="text-sm space-y-1">
                                        <p className="font-medium text-slate-900">
                                            {(rental.equipment_type || "").replace("_", " ").toUpperCase() || "Rental"}
                                            {rental.unit_number && <span className="text-slate-500"> · #{rental.unit_number}</span>}
                                        </p>
                                        <p className="text-xs text-slate-500">{[rental.year, rental.make, rental.model].filter(Boolean).join(" ") || "—"}</p>
                                        <p className="text-xs text-slate-500">Status: <span className="font-medium text-slate-700">{rental.status}</span></p>
                                        {formatRateChip(rental) && (
                                            <p className="text-xs font-medium text-slate-700" data-testid="contract-rental-rate-chip">{formatRateChip(rental)}</p>
                                        )}
                                        <div className="flex gap-2 pt-2">
                                            <Button asChild size="sm" variant="outline" className="gap-1.5">
                                                <Link to={`/admin/rentals/${rental.id}`} data-testid="contract-open-rental">
                                                    Open rental <ChevronLeft size={14} className="rotate-180" />
                                                </Link>
                                            </Button>
                                            {isDraft && (
                                                <Button size="sm" variant="outline" onClick={() => setShowRental(true)}
                                                    data-testid="contract-edit-rental">Edit</Button>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <p className="text-xs text-slate-500">
                                        No rental attached yet. Attach now and the rental will auto-progress when this contract is fully signed.
                                    </p>
                                )}
                            </CardContent>
                        </Card>
                    )}

                    <Card>
                        <CardContent className="p-5 space-y-4">
                            {(() => {
                                // Bug 2 — collapse SETTINGS by default once the contract
                                // is fully signed. Pre-signature contracts default to
                                // expanded (fields are still editable). User toggle pins it.
                                const isFullySigned = c.status === "fully_signed";
                                const collapsedByDefault = isFullySigned;
                                const expanded = settingsExpanded === null ? !collapsedByDefault : settingsExpanded;
                                return (
                                    <>
                                        <button
                                            type="button"
                                            onClick={() => setSettingsExpanded(!expanded)}
                                            data-testid="contract-settings-toggle"
                                            aria-expanded={expanded}
                                            className="flex items-center gap-2 w-full text-left -mx-1 -my-1 px-1 py-1 rounded hover:bg-slate-50/60 transition-colors"
                                        >
                                            {expanded
                                                ? <ChevronDown size={14} className="text-slate-500 shrink-0" />
                                                : <ChevronRight size={14} className="text-slate-500 shrink-0" />}
                                            <p className="text-xs font-medium tracking-[0.18em] uppercase text-slate-500">Settings</p>
                                            {!expanded && isFullySigned && (
                                                <span className="ml-auto text-[10px] text-slate-400 italic" data-testid="contract-settings-collapsed-hint">
                                                    immutable · click to view
                                                </span>
                                            )}
                                        </button>
                                        {expanded && (
                                            <div className="space-y-4" data-testid="contract-settings-body">
                            <div className="space-y-1.5">
                                <Label htmlFor="contract-title-input">Title</Label>
                                <Input id="contract-title-input" value={title} onChange={(e) => setTitle(e.target.value)}
                                    disabled={!isDraft} data-testid="contract-title-input" />
                            </div>
                            <div className="space-y-1.5">
                                <Label>Contract Template</Label>
                                <select className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-900/30 disabled:bg-slate-50"
                                    value={templateId} onChange={(e) => setTemplateId(e.target.value)} disabled={!isDraft}
                                    data-testid="contract-template-select">
                                    <option value="">(no template — fallback layout)</option>
                                    {templates.map((t) => (
                                        <option key={t.id} value={t.id}>{t.name}</option>
                                    ))}
                                </select>
                            </div>
                            {/* Phase H.14b — ACH Template dropdown sits between
                                Contract Template and COI requirements. Same
                                draft-only editability gate. The empty option
                                value means "no ACH bundle"; the PATCH handler
                                removes the corresponding ach_authorization
                                doc from documents[]. */}
                            <div className="space-y-1.5">
                                <Label>ACH Template</Label>
                                <select className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-900/30 disabled:bg-slate-50"
                                    value={achTemplateId} onChange={(e) => setAchTemplateId(e.target.value)} disabled={!isDraft}
                                    data-testid="contract-ach-template-select">
                                    <option value="">— None (no ACH bundle) —</option>
                                    {achTemplates.map((t) => (
                                        <option key={t.id} value={t.id}>{t.name}</option>
                                    ))}
                                </select>
                                <p className="text-xs text-slate-500">Snapshotted onto this contract when sent for signature.</p>
                            </div>
                            {c.type === "rental_agreement" && (
                                <div className="space-y-1.5">
                                    <Label>COI requirements</Label>
                                    <select className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-900/30 disabled:bg-slate-50"
                                        value={coiTemplateId} onChange={(e) => setCoiTemplateId(e.target.value)} disabled={!isDraft}
                                        data-testid="contract-coi-template-select">
                                        <option value="">(none — auto-select default)</option>
                                        {coiTemplates.map((t) => (
                                            <option key={t.id} value={t.id}>{t.name}</option>
                                        ))}
                                    </select>
                                    <p className="text-xs text-slate-500">Snapshotted onto this contract when sent for signature. Drives the COI request PDF.</p>
                                </div>
                            )}
                            <div className="space-y-1.5">
                                <Label htmlFor="contract-notes-input">Notes (admin-only)</Label>
                                <Textarea id="contract-notes-input" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
                                    disabled={!isDraft} data-testid="contract-notes-input" />
                            </div>
                            {isDraft && (
                                <Button onClick={onSavePatch} disabled={busy} size="sm" className="bg-blue-900 hover:bg-blue-800 gap-1.5 w-full"
                                    data-testid="contract-save"><Save size={14} /> Save changes</Button>
                            )}
                                        </div>
                                        )}
                                    </>
                                );
                            })()}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent className="p-5 space-y-3" data-testid="contract-signatures-panel">
                            <p className="text-xs font-medium tracking-[0.18em] uppercase text-slate-500">Signatures</p>
                            <div className="text-sm">
                                <p className="font-medium text-slate-900">Customer</p>
                                {sigCustomer ? (
                                    <div className="text-slate-700 mt-0.5">
                                        {sigCustomer.signer_name} · {sigCustomer.method} · {fmt(sigCustomer.signed_at)}
                                    </div>
                                ) : <p className="text-slate-400 text-xs italic">Not yet signed</p>}
                            </div>
                            <div className="text-sm border-t border-slate-100 pt-3">
                                <p className="font-medium text-slate-900">Admin (countersignature)</p>
                                {sigAdmin ? (
                                    <div className="text-slate-700 mt-0.5">
                                        {sigAdmin.signer_name} · {sigAdmin.method} · {fmt(sigAdmin.signed_at)}
                                    </div>
                                ) : <p className="text-slate-400 text-xs italic">Pending</p>}
                            </div>
                        </CardContent>
                    </Card>

                    {c.status === "fully_signed" && c.has_signed_pdf && (
                        <Button variant="outline" className="w-full gap-1.5" data-testid="contract-download-signed"
                            onClick={async () => {
                                try {
                                    // Phase N.11 (2026-02) — admin download now
                                    // fetches the signed RA bundled with Appendix
                                    // A (falls back to just-RA on contracts
                                    // without an Appendix A doc).
                                    const resp = await api.get(`/admin/contracts/${id}/pdf`, { params: { variant: "signed_with_appendix_a" }, responseType: "blob" });
                                    const blob = new Blob([resp.data], { type: "application/pdf" });
                                    const url = URL.createObjectURL(blob);
                                    const a = document.createElement("a");
                                    a.href = url; a.download = `${c.title}-signed-with-appendix.pdf`; a.click();
                                    URL.revokeObjectURL(url);
                                    const warning = resp.headers?.["x-rentflow-warning"];
                                    if (warning === "appendix_a_not_present") {
                                        toast.message("Signed rental agreement downloaded. This contract has no Appendix A.");
                                    }
                                } catch (err) { toast.error(formatApiError(err)); }
                            }}>
                            <Download size={14} /> Download signed PDF
                        </Button>
                    )}

                    <Button variant="outline" className="w-full gap-1.5" data-testid="contract-download-audit-json"
                        onClick={async () => {
                            try {
                                const resp = await api.get(`/admin/contracts/${id}/audit-trail.json`, { responseType: "blob" });
                                const blob = new Blob([resp.data], { type: "application/json" });
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement("a");
                                a.href = url; a.download = `${c.title || "contract"}-audit-trail.json`; a.click();
                                URL.revokeObjectURL(url);
                            } catch (err) { toast.error(formatApiError(err)); }
                        }}>
                        <Download size={14} /> Download audit trail (JSON)
                    </Button>

                    {c.status === "cancelled" && (
                        <Card>
                            <CardContent className="p-5 text-sm" data-testid="contract-cancel-reason">
                                <p className="text-xs font-medium tracking-[0.18em] uppercase text-red-700 mb-2">Cancelled</p>
                                <p className="text-slate-700">{c.cancel_reason}</p>
                                <p className="text-xs text-slate-500 mt-2">{fmt(c.cancelled_at)}</p>
                            </CardContent>
                        </Card>
                    )}
                </div>
            </div>

            {/* Phase E1 — Audit Trail */}
            <Card className="mt-6" data-testid="contract-audit-trail">
                <CardContent className="p-5">
                    {(() => {
                        // Bug 3 — collapse by default for ALL contracts. The header keeps
                        // the integrity pills (chain + RFC 3161) visible so admins can
                        // verify at a glance without expanding the full event table.
                        const expanded = auditExpanded === null ? false : auditExpanded;
                        const eventCount = events.length;
                        return (
                            <>
                                <button
                                    type="button"
                                    onClick={() => setAuditExpanded(!expanded)}
                                    data-testid="contract-audit-toggle"
                                    aria-expanded={expanded}
                                    className="flex items-start justify-between gap-3 flex-wrap w-full text-left -mx-1 -my-1 px-1 py-1 rounded hover:bg-slate-50/60 transition-colors"
                                >
                                    <div className="flex items-start gap-2 flex-1 min-w-0">
                                        {expanded
                                            ? <ChevronDown size={14} className="text-slate-500 shrink-0 mt-1.5" />
                                            : <ChevronRight size={14} className="text-slate-500 shrink-0 mt-1.5" />}
                                        <div>
                                            <p className="text-[11px] font-medium tracking-[0.18em] uppercase text-blue-900 inline-flex items-center gap-1.5">
                                                <ShieldCheck size={12} /> ESIGN/UETA · Audit Trail
                                            </p>
                                            <h2 className="font-display text-lg font-semibold text-slate-900 mt-1 flex items-center gap-2">
                                                Event timeline
                                                {eventCount > 0 && (
                                                    <span
                                                        data-testid="contract-audit-event-count"
                                                        className="inline-flex items-center rounded-full bg-slate-100 text-slate-700 text-[10px] font-semibold px-2 py-0.5 ring-1 ring-inset ring-slate-200"
                                                    >
                                                        {eventCount} {eventCount === 1 ? "event" : "events"}
                                                    </span>
                                                )}
                                            </h2>
                                            <p className="text-xs text-slate-500 mt-0.5">Every link open, view, consent, signature, and admin action.</p>
                                        </div>
                                    </div>
                                    {pdfSha && (
                                        <div className="text-[10px] font-mono bg-slate-50 border border-slate-200 rounded-md px-2 py-1 text-slate-700 max-w-md break-all">
                                            Signed PDF SHA-256: {pdfSha}
                                        </div>
                                    )}
                                </button>

                                {/* Phase E2 — chain + timestamp status pills. Always visible
                                    (even when the table is collapsed) so admins can verify
                                    integrity at a glance without expanding the full event list. */}
                    <div className="mt-4 flex items-center gap-2 flex-wrap" data-testid="contract-audit-pills">
                        <ChainPill chain={chain} verifying={verifyingChain} onVerify={async () => {
                            setVerifyingChain(true);
                            try {
                                const r = await api.get(`/admin/contracts/${id}/audit-trail/verify`);
                                setChain(r.data);
                                if (r.data.valid) toast.success(`Hash chain verified — ${r.data.total_events} events`);
                                else toast.error(`Chain broken at event ${String(r.data.broken_at_event_id).slice(0, 8)}`);
                            } catch (err) { toast.error(formatApiError(err)); }
                            finally { setVerifyingChain(false); }
                        }} />
                        <TimestampPill rfc3161={rfc3161} verifying={verifyingTs} retrying={retryingTs} onVerify={async () => {
                            setVerifyingTs(true);
                            try {
                                const r = await api.post(`/admin/contracts/${id}/timestamp/verify`);
                                // Phase F-trust — endpoint now returns {results, all_valid} aggregate
                                const primary = (r.data?.results && r.data.results[0]) || r.data;
                                setRfc3161((prev) => ({ ...(prev || {}), valid: primary?.valid, signed_at_per_tsa: primary?.signed_at_per_tsa, tsa_serial: primary?.tsa_serial, tsa_subject: primary?.tsa_subject }));
                                if (r.data?.all_valid || primary?.valid) toast.success(`TSA token${(r.data?.results?.length || 1) > 1 ? "s" : ""} verified — message imprint matches signed PDF`);
                                else toast.error(`Timestamp not valid: ${primary?.reason || "unknown"}`);
                            } catch (err) { toast.error(formatApiError(err)); }
                            finally { setVerifyingTs(false); }
                        }} onRetry={async () => {
                            setRetryingTs(true);
                            try {
                                const r = await api.post(`/admin/contracts/${id}/timestamp/retry`);
                                toast.success(`TSA token obtained · serial ${r.data?.tsa_serial}`);
                                await reload();
                            } catch (err) { toast.error(formatApiError(err)); }
                            finally { setRetryingTs(false); }
                        }} />
                    </div>

                    {expanded && (
                    <div data-testid="contract-audit-body">
                    {/* Phase F-trust — per-document trust rows when multi-doc */}
                    {documentsTrust && documentsTrust.length > 1 && (
                        <div className="mt-3 space-y-2" data-testid="contract-per-doc-trust">
                            {documentsTrust.map((d) => {
                                const tsValid = d.rfc3161?.valid === true;
                                const tsStatus = d.rfc3161?.status || "none";
                                // Phase N.9b-hotfix (2026-02) — explicit label map;
                                // previously any non-rental_agreement doc rendered
                                // as "ACH authorization", which mislabeled
                                // Appendix A (companion doc) after the N.9a
                                // backfill against prod contracts.
                                const _labels = {
                                    rental_agreement: "Rental contract",
                                    ach_authorization: "ACH authorization",
                                    appendix_a: "Appendix A",
                                    coi: "Certificate of Insurance",
                                    personal_guaranty: "Personal Guaranty",
                                };
                                const label = _labels[d.doc_type] || (d.doc_type || "Document").replace(/_/g, " ");
                                const verifyDoc = async () => {
                                    try {
                                        const r = await api.post(`/admin/contracts/${id}/timestamp/verify`, null, { params: { doc_id: d.doc_id } });
                                        if (r.data?.valid) toast.success(`${label} timestamp verified`);
                                        else toast.error(`${label} timestamp invalid: ${r.data?.reason || "unknown"}`);
                                    } catch (err) { toast.error(formatApiError(err)); }
                                };
                                return (
                                    <div key={d.doc_id}
                                        data-testid={`per-doc-trust-${d.doc_type}`}
                                        className="flex items-center gap-2 flex-wrap rounded-md border border-slate-200 bg-slate-50/40 px-3 py-2 text-xs">
                                        <span className="font-semibold text-slate-700 mr-1">{label}:</span>
                                        {/* Phase N.9b remainder (2026-02) — flag
                                            companion docs (Appendix A backfilled
                                            after original signing) so ops can
                                            distinguish original-signed vs
                                            regenerated schedules at a glance. */}
                                        {d.is_companion && (
                                            <span
                                                data-testid={`per-doc-companion-tag-${d.doc_type}`}
                                                title="Backfilled after original signing; hash-chain preserved"
                                                className="inline-flex items-center rounded-full bg-indigo-50 text-indigo-900 ring-1 ring-inset ring-indigo-200 px-2 py-0.5 font-semibold">
                                                Companion doc
                                            </span>
                                        )}
                                        <span
                                            data-testid={`per-doc-pill-${d.doc_type}`}
                                            data-trust-valid={tsValid ? "true" : "false"}
                                            className={
                                                "inline-flex items-center rounded-full px-2 py-0.5 font-semibold ring-1 ring-inset " +
                                                (tsValid
                                                    ? "bg-emerald-50 text-emerald-900 ring-emerald-200"
                                                    : tsStatus === "failed"
                                                        ? "bg-amber-50 text-amber-900 ring-amber-200"
                                                        : "bg-slate-100 text-slate-700 ring-slate-200")
                                            }>
                                            <ShieldCheck size={11} className="mr-1" />
                                            {tsValid
                                                ? `chain valid · timestamp ${d.rfc3161?.signed_at_per_tsa ? d.rfc3161.signed_at_per_tsa.slice(0, 19).replace("T", " ") : "valid"}`
                                                : tsStatus === "failed"
                                                    ? "timestamp failed"
                                                    : "timestamp pending"}
                                        </span>
                                        <span className="font-mono text-[10px] text-slate-500 ml-auto break-all max-w-[300px]"
                                            data-testid={`per-doc-sha-${d.doc_type}`}>
                                            sha …{(d.signed_pdf_sha256 || "").slice(-12) || "—"}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={verifyDoc}
                                            data-testid={`per-doc-verify-${d.doc_type}`}
                                            className="ml-2 underline text-blue-700 hover:text-blue-900 text-[11px]">
                                            verify
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    <div className="mt-3 -mx-5 overflow-x-auto">
                        <table className="w-full text-xs" data-testid="contract-audit-table">
                            <thead className="bg-slate-50 text-slate-600">
                                <tr>
                                    <th className="text-left px-5 py-2 font-medium whitespace-nowrap">Time</th>
                                    <th className="text-left px-3 py-2 font-medium whitespace-nowrap">Event</th>
                                    <th className="text-left px-3 py-2 font-medium whitespace-nowrap">Actor</th>
                                    <th className="text-left px-3 py-2 font-medium whitespace-nowrap">IP</th>
                                    <th className="text-left px-3 py-2 font-medium">User agent</th>
                                </tr>
                            </thead>
                            <tbody>
                                {events.map((e) => (
                                    <tr key={e.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`contract-audit-row-${e.event_type}`}>
                                        <td className="px-5 py-2 whitespace-nowrap text-slate-700">{fmt(e.created_at)}</td>
                                        <td className="px-3 py-2 font-medium text-slate-900">
                                            {e.event_type === "tamper_detected" ? (
                                                <span className="inline-flex items-center gap-1 text-red-700">
                                                    <AlertTriangle size={12} /> {e.event_type.replace(/_/g, " ")}
                                                </span>
                                            ) : (
                                                <span>{e.event_type.replace(/_/g, " ")}</span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap text-slate-700">
                                            {e.actor_role}{e.actor_id ? ` #${String(e.actor_id).slice(0, 8)}` : ""}
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap text-slate-700 font-mono">{e.ip_address || "—"}</td>
                                        <td className="px-3 py-2 text-slate-500 text-[10px] truncate max-w-xs">{e.user_agent || "—"}</td>
                                    </tr>
                                ))}
                                {events.length === 0 && (
                                    <tr><td colSpan={5} className="px-5 py-6 text-center text-slate-500">No events recorded yet.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    </div>
                    )}
                            </>
                        );
                    })()}
                </CardContent>
            </Card>

            <Dialog open={showCancel} onOpenChange={setShowCancel}>
                <DialogContent data-testid="contract-cancel-dialog" className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Cancel contract</DialogTitle>
                        <DialogDescription>
                            This is final. Pick a path below — the customer&apos;s view will reflect it immediately.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-1.5">
                            <Label htmlFor="cancel-reason">
                                Reason <span className="text-red-500">*</span>
                                <span className="text-xs text-slate-500 ml-2">(min 8 characters)</span>
                            </Label>
                            <Textarea
                                id="cancel-reason"
                                rows={3}
                                value={cancelReason}
                                onChange={(e) => setCancelReason(e.target.value)}
                                placeholder="Customer changed equipment / credit decision / other"
                                data-testid="contract-cancel-reason-input"
                            />
                            <div className="flex items-center gap-2 pt-1">
                                <input
                                    id="cancel-show-reason"
                                    type="checkbox"
                                    checked={cancelShowToCustomer}
                                    onChange={(e) => setCancelShowToCustomer(e.target.checked)}
                                    data-testid="contract-cancel-show-reason"
                                    className="h-4 w-4"
                                />
                                <Label htmlFor="cancel-show-reason" className="cursor-pointer text-sm font-normal text-slate-700">
                                    Show this reason on the customer&apos;s contract view
                                </Label>
                            </div>
                        </div>

                        <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
                            <Label className="text-sm font-medium text-slate-900">After cancellation</Label>
                            <label
                                htmlFor="cancel-action-reassign"
                                className={`flex cursor-pointer items-start gap-3 rounded-md border p-3 transition ${
                                    cancelAction === "reassign"
                                        ? "border-blue-500 bg-white shadow-sm"
                                        : "border-transparent bg-white/60 hover:bg-white"
                                }`}
                            >
                                <input
                                    id="cancel-action-reassign"
                                    type="radio"
                                    name="cancel-action"
                                    value="reassign"
                                    checked={cancelAction === "reassign"}
                                    onChange={() => setCancelAction("reassign")}
                                    data-testid="contract-cancel-action-reassign"
                                    className="mt-1 h-4 w-4"
                                />
                                <div className="flex-1">
                                    <p className="text-sm font-medium text-slate-900">Reassign equipment <span className="text-xs font-normal text-slate-500">(default)</span></p>
                                    <p className="text-xs text-slate-600">
                                        Free the equipment, drop the draft rental, keep the customer&apos;s application alive.
                                        You&apos;ll pick new equipment from their submission page.
                                    </p>
                                </div>
                            </label>
                            <label
                                htmlFor="cancel-action-archive"
                                className={`flex cursor-pointer items-start gap-3 rounded-md border p-3 transition ${
                                    cancelAction === "archive"
                                        ? "border-blue-500 bg-white shadow-sm"
                                        : "border-transparent bg-white/60 hover:bg-white"
                                }`}
                            >
                                <input
                                    id="cancel-action-archive"
                                    type="radio"
                                    name="cancel-action"
                                    value="archive"
                                    checked={cancelAction === "archive"}
                                    onChange={() => setCancelAction("archive")}
                                    data-testid="contract-cancel-action-archive"
                                    className="mt-1 h-4 w-4"
                                />
                                <div className="flex-1">
                                    <p className="text-sm font-medium text-slate-900">Archive submission</p>
                                    <p className="text-xs text-slate-600">
                                        Free the equipment + close out the customer&apos;s application. They&apos;ll see the
                                        cancellation reason and be told to contact you for a new application.
                                    </p>
                                </div>
                            </label>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCancel(false)} data-testid="contract-cancel-back">Back</Button>
                        <Button
                            onClick={onCancel}
                            disabled={busy || cancelReason.trim().length < 8}
                            className="bg-red-600 hover:bg-red-700"
                            data-testid="contract-cancel-confirm"
                        >
                            {cancelAction === "reassign" ? "Cancel & reassign" : "Cancel & archive"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={showCountersign} onOpenChange={setShowCountersign}>
                <DialogContent className="max-w-xl" data-testid="contract-countersign-dialog">
                    <DialogHeader>
                        <DialogTitle>Countersign contract</DialogTitle>
                        <DialogDescription>The customer has signed. Add your countersignature to finalize.</DialogDescription>
                    </DialogHeader>
                    <ContractSignaturePad
                        defaultSignerName={user?.full_name || ""}
                        onSign={onCountersign}
                        busy={busy}
                        ctaLabel="Countersign and finalize"
                        testIdPrefix="contract-countersign-pad"
                    />
                </DialogContent>
            </Dialog>

            {/* Bug 4 — Send-for-signature review modal. Opens BEFORE the
                send API fires (except for the zero-deposit waiver branch,
                which has its own hard-confirm modal). Pulls the snapshot
                from the already-loaded contract + rental + customer state
                — no new API calls beyond a one-shot customer fetch. */}
            <Dialog
                open={showSendConfirm}
                onOpenChange={(open) => {
                    if (!busy) { setShowSendConfirm(open); if (!open) setSendError(null); }
                }}
            >
                <DialogContent
                    className="max-w-2xl"
                    data-testid="contract-send-confirm-dialog"
                    onKeyDown={(e) => {
                        // Enter = Confirm & send (only when focus is NOT on Edit rental).
                        if (e.key === "Enter" && !busy) {
                            const focused = document.activeElement?.getAttribute?.("data-testid");
                            if (focused !== "contract-send-confirm-edit-rental") {
                                e.preventDefault();
                                doSend();
                            }
                        }
                    }}
                >
                    <DialogHeader>
                        <DialogTitle>Send contract for signature?</DialogTitle>
                        <DialogDescription>
                            Review the rental snapshot below. Once sent, these values are locked onto the contract.
                        </DialogDescription>
                    </DialogHeader>
                    {sendError && (
                        <div
                            className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900 flex items-start gap-2"
                            data-testid="contract-send-confirm-error"
                        >
                            <AlertTriangle size={16} className="shrink-0 mt-0.5 text-red-700" />
                            <span className="break-words">{sendError}</span>
                        </div>
                    )}
                    <SendConfirmSnapshot
                        contract={c}
                        rental={rental}
                        customer={customer}
                        templates={templates}
                        achTemplates={achTemplates}
                        coiTemplates={coiTemplates}
                        templateId={templateId}
                        achTemplateId={achTemplateId}
                        coiTemplateId={coiTemplateId}
                        onFixSetup={() => {
                            // Close + scroll to the SETTINGS card so the
                            // admin can pick a template / ACH / COI right
                            // there. Settings is collapsed in fully_signed
                            // contracts but a draft is always expanded so
                            // a simple scroll works without re-toggling.
                            setShowSendConfirm(false);
                            setTimeout(() => {
                                const el = document.querySelector('[data-testid="contract-settings-toggle"]');
                                if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
                            }, 100);
                        }}
                        onFixRental={() => {
                            setShowSendConfirm(false);
                            setShowRental(true);
                        }}
                    />
                    <DialogFooter className="gap-2">
                        <Button
                            variant="outline"
                            onClick={() => { if (!busy) { setShowSendConfirm(false); setSendError(null); } }}
                            disabled={busy}
                            data-testid="contract-send-confirm-cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => {
                                if (busy) return;
                                setShowSendConfirm(false);
                                setShowRental(true);
                            }}
                            disabled={busy}
                            data-testid="contract-send-confirm-edit-rental"
                        >
                            Edit rental
                        </Button>
                        <Button
                            onClick={() => doSend()}
                            disabled={busy}
                            className="bg-blue-900 hover:bg-blue-800 gap-1.5"
                            data-testid="contract-send-confirm-confirm"
                        >
                            <Send size={14} /> {busy ? "Sending…" : "Confirm & send"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={showRental} onOpenChange={setShowRental}>
                <DialogContent className="max-w-2xl" data-testid="contract-rental-dialog">
                    <DialogHeader>
                        <DialogTitle>{rental ? "Edit rental" : "Attach rental"}</DialogTitle>
                        <DialogDescription>Equipment + dates for this rental. The rental moves to <em>contract_signed</em> the moment this contract is fully signed.</DialogDescription>
                    </DialogHeader>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                            <Label>Equipment type</Label>
                            <select value={rentalForm.equipment_type} onChange={(e) => setRentalForm({ ...rentalForm, equipment_type: e.target.value })}
                                className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
                                data-testid="contract-rental-equipment">
                                <option value="">— Select —</option>
                                <option value="reefer">Reefer</option>
                                <option value="dry_van">Dry Van</option>
                                <option value="conestoga">Conestoga</option>
                                <option value="flatbed">Flatbed</option>
                                <option value="step_deck">Step Deck</option>
                                <option value="other">Other</option>
                            </select>
                        </div>
                        <RentalField label="Unit #" v={rentalForm.unit_number} onChange={(v) => setRentalForm({ ...rentalForm, unit_number: v })} testId="contract-rental-unit" />
                        <RentalField label="Make" v={rentalForm.make} onChange={(v) => setRentalForm({ ...rentalForm, make: v })} testId="contract-rental-make" />
                        <RentalField label="Model" v={rentalForm.model} onChange={(v) => setRentalForm({ ...rentalForm, model: v })} testId="contract-rental-model" />
                        <RentalField label="Year" type="number" v={rentalForm.year} onChange={(v) => setRentalForm({ ...rentalForm, year: v })} testId="contract-rental-year" />
                        <RentalField label="VIN" v={rentalForm.vin} onChange={(v) => setRentalForm({ ...rentalForm, vin: v })} testId="contract-rental-vin" />
                        <RentalField label="Plate" v={rentalForm.plate} onChange={(v) => setRentalForm({ ...rentalForm, plate: v })} testId="contract-rental-plate" />
                        <RentalField label="Start date" type="date" v={rentalForm.start_date} onChange={(v) => setRentalForm({ ...rentalForm, start_date: v })} testId="contract-rental-start" />
                        <RentalField label="End date (optional)" type="date" v={rentalForm.end_date} onChange={(v) => setRentalForm({ ...rentalForm, end_date: v })} testId="contract-rental-end" />
                        <RentalField label="Daily rate ($)" type="number" v={rentalForm.daily_rate} onChange={(v) => setRentalForm({ ...rentalForm, daily_rate: v })} testId="contract-rental-daily-rate" />
                        <RentalField label="Monthly rate ($)" type="number" v={rentalForm.monthly_rate} onChange={(v) => setRentalForm({ ...rentalForm, monthly_rate: v })} testId="contract-rental-monthly-rate" />
                        <div className="space-y-1.5">
                            <Label>Billing period</Label>
                            <div className="flex gap-2" data-testid="contract-rental-billing-period">
                                {["daily", "monthly"].map((p) => {
                                    const checked = (rentalForm.billing_period || (rentalForm.monthly_rate && !rentalForm.daily_rate ? "monthly" : (rentalForm.daily_rate ? "daily" : ""))) === p;
                                    return (
                                        <button key={p} type="button"
                                            onClick={() => setRentalForm({ ...rentalForm, billing_period: p })}
                                            data-testid={`contract-rental-billing-${p}`}
                                            className={`flex-1 h-10 rounded-md border text-sm font-medium transition-colors ${
                                                checked ? "bg-blue-900 border-blue-900 text-white" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
                                            }`}>{p === "daily" ? "Daily" : "Monthly"}</button>
                                    );
                                })}
                            </div>
                        </div>
                        <RentalField label="Min term (months)" type="number" v={rentalForm.minimum_term_months} onChange={(v) => setRentalForm({ ...rentalForm, minimum_term_months: v })} testId="contract-rental-min-term"
                            helper="Customer commits to this minimum length when monthly pricing is selected. Optional." />
                        <div className="space-y-1.5">
                            <Label>Deposit ($)</Label>
                            <Input
                                type="number"
                                value={rentalForm.deposit ?? ""}
                                onChange={(e) => setRentalForm({ ...rentalForm, deposit: e.target.value })}
                                data-testid="contract-rental-deposit"
                            />
                            {/* Task 17 — soft warning when deposit is blank/zero AND a default exists for the selected equipment type */}
                            {(() => {
                                const slug = rentalForm.equipment_type;
                                const depNum = Number(rentalForm.deposit);
                                const isZeroOrEmpty = !rentalForm.deposit || !Number.isFinite(depNum) || depNum <= 0;
                                const defaultCents = slug ? Number(defaultDeposits?.[slug] || 0) : 0;
                                if (!isZeroOrEmpty || !defaultCents) return null;
                                const dollars = (defaultCents / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });
                                return (
                                    <div
                                        className="mt-1 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
                                        data-testid="rental-deposit-warning-banner"
                                    >
                                        <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                                        <div className="flex-1">
                                            <p>
                                                No deposit set. The default for <strong>{slug.replace(/_/g, " ")}</strong> is <strong>{dollars}</strong>.
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setRentalForm({ ...rentalForm, deposit: String(defaultCents / 100) })}
                                            className="rounded bg-amber-900 text-white text-[11px] font-semibold px-2 py-1 hover:bg-amber-800"
                                            data-testid="rental-deposit-use-default"
                                        >
                                            Use default
                                        </button>
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                    {(rentalForm.daily_rate || rentalForm.monthly_rate) && (
                        <div className="mt-3 rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-700"
                            data-testid="contract-rental-pricing-preview">
                            {buildRatePreview({
                                daily_rate: rentalForm.daily_rate ? Number(rentalForm.daily_rate) : null,
                                monthly_rate: rentalForm.monthly_rate ? Number(rentalForm.monthly_rate) : null,
                                minimum_term_months: rentalForm.minimum_term_months ? Number(rentalForm.minimum_term_months) : null,
                                billing_period: rentalForm.billing_period || null,
                                start_date: rentalForm.start_date,
                            })}
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowRental(false)}>Cancel</Button>
                        <Button onClick={onSaveRental} disabled={busy} className="bg-blue-900 hover:bg-blue-800"
                            data-testid="contract-rental-save">{rental ? "Save rental" : "Attach rental"}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Task 17 — Zero-deposit hard-confirm modal */}
            <Dialog open={showZeroDepositModal} onOpenChange={setShowZeroDepositModal}>
                <DialogContent className="max-w-lg" data-testid="zero-deposit-modal">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-amber-900">
                            <AlertTriangle size={18} />
                            Send contract with $0 deposit?
                        </DialogTitle>
                        <DialogDescription>
                            This contract will be sent to <strong>{rental?.deposit_warning?.equipment_type
                                ? `the customer (${rental.deposit_warning.equipment_type.replace(/_/g, " ")})`
                                : "the customer"}</strong> with <strong>no security deposit</strong>.
                            {(() => {
                                const slug = rental?.deposit_warning?.equipment_type;
                                const cents = slug ? Number(defaultDeposits?.[slug] || rental?.deposit_warning?.suggested_amount_cents || 0) : 0;
                                if (!cents) return null;
                                const dollars = (cents / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });
                                return (
                                    <span> The default for <strong>{(slug || "").replace(/_/g, " ")}</strong> is <strong>{dollars}</strong>.</span>
                                );
                            })()}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                        Either set a deposit now or confirm the waiver below — every $0-deposit send is logged for audit.
                    </div>
                    <div className="space-y-3 mt-2">
                        <div className="space-y-1.5">
                            <Label htmlFor="zero-deposit-confirm">Type <span className="font-mono font-semibold">CONFIRM</span> to acknowledge</Label>
                            <Input
                                id="zero-deposit-confirm"
                                value={zeroDepositConfirmText}
                                onChange={(e) => setZeroDepositConfirmText(e.target.value)}
                                placeholder="CONFIRM"
                                data-testid="zero-deposit-confirm-input"
                                autoComplete="off"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="zero-deposit-reason">Reason (min 8 characters) <span className="text-red-500">*</span></Label>
                            <Textarea
                                id="zero-deposit-reason"
                                rows={3}
                                value={zeroDepositReason}
                                onChange={(e) => setZeroDepositReason(e.target.value)}
                                placeholder="e.g. Long-standing customer, deposit waived per VP approval"
                                data-testid="zero-deposit-reason-input"
                            />
                            <p className="text-[10px] text-slate-500">
                                {zeroDepositReason.trim().length}/8 minimum
                            </p>
                        </div>
                    </div>
                    <DialogFooter className="gap-2 sm:gap-2">
                        <Button
                            variant="outline"
                            onClick={() => { setShowZeroDepositModal(false); setShowRental(true); }}
                            data-testid="zero-deposit-set-deposit"
                        >
                            Set deposit instead
                        </Button>
                        <Button
                            onClick={() => onSend({ confirmed: true, reason: zeroDepositReason.trim() })}
                            disabled={
                                busy
                                || zeroDepositConfirmText.trim() !== "CONFIRM"
                                || zeroDepositReason.trim().length < 8
                            }
                            className="bg-amber-900 hover:bg-amber-800"
                            data-testid="zero-deposit-confirm-send"
                        >
                            <Send size={14} className="mr-1.5" />
                            Send anyway
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Phase IMPORT-PAPER-REPLACE — replace paper contract file modal */}
            <Dialog open={showReplacePaper} onOpenChange={(o) => { setShowReplacePaper(o); if (!o) setReplacePaperFile(null); }}>
                <DialogContent data-testid="replace-paper-contract-dialog">
                    <DialogHeader>
                        <DialogTitle>Replace paper contract file</DialogTitle>
                        <DialogDescription>
                            Upload a new PDF or DOCX to replace the current paper contract. The existing file will be archived and remain queryable in the audit history — it&apos;s never deleted.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <label className="block text-xs font-medium uppercase tracking-wide text-slate-500">
                            New paper contract file
                        </label>
                        <input
                            type="file"
                            accept="application/pdf,.pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.docx"
                            data-testid="replace-paper-contract-file-input"
                            onChange={(e) => setReplacePaperFile(e.target.files?.[0] || null)}
                            className="block w-full text-sm text-slate-700 file:mr-3 file:rounded file:border-0 file:bg-slate-200 file:px-3 file:py-1.5 file:text-slate-900 hover:file:bg-slate-300"
                        />
                        {replacePaperFile && (
                            <p className="text-xs text-slate-600">
                                Selected: <span className="font-mono">{replacePaperFile.name}</span> ({Math.round(replacePaperFile.size / 1024)} KB)
                            </p>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowReplacePaper(false)} disabled={busy} data-testid="replace-paper-contract-cancel">
                            Cancel
                        </Button>
                        <Button
                            onClick={onReplacePaperContract}
                            disabled={busy || !replacePaperFile}
                            className="bg-blue-900 hover:bg-blue-800"
                            data-testid="replace-paper-contract-confirm"
                        >
                            {busy ? "Replacing…" : "Replace file"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </DashboardShell>
    );
}

function RentalField({ label, v, onChange, type = "text", testId, helper }) {
    return (
        <div className="space-y-1.5">
            <Label>{label}</Label>
            <Input type={type} value={v ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
            {helper && <p className="text-[10px] text-slate-500 leading-tight">{helper}</p>}
        </div>
    );
}


// Phase E2 — small status pills for hash-chain + RFC 3161 trusted timestamp.
function ChainPill({ chain, verifying, onVerify }) {
    const status = chain == null ? "unknown" : chain.valid ? "ok" : "broken";
    const tone = status === "ok" ? "bg-emerald-50 text-emerald-800 ring-emerald-200"
        : status === "broken" ? "bg-red-50 text-red-800 ring-red-200"
        : "bg-slate-100 text-slate-600 ring-slate-200";
    const label = status === "ok" ? `Hash chain valid · ${chain?.total_events ?? 0} events`
        : status === "broken" ? `Hash chain broken at ${String(chain?.broken_at_event_id || "").slice(0, 8)}`
        : "Hash chain: unknown";
    return (
        <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-semibold ring-1 ${tone}`}
            data-testid="contract-chain-pill">
            {status === "broken" ? <AlertTriangle size={12} /> : <ShieldCheck size={12} />}
            <span data-testid="contract-chain-pill-label">{label}</span>
            <button
                type="button"
                onClick={onVerify}
                disabled={verifying}
                className="underline decoration-dotted hover:decoration-solid text-[10px]"
                data-testid="contract-chain-verify">
                {verifying ? "verifying…" : "verify"}
            </button>
        </span>
    );
}

function TimestampPill({ rfc3161, verifying, retrying, onVerify, onRetry }) {
    const status = rfc3161?.status || "none";
    const valid = rfc3161?.valid;
    let tone = "bg-slate-100 text-slate-600 ring-slate-200";
    let label = "Trusted timestamp: none";
    if (status === "ok" && valid) {
        tone = "bg-emerald-50 text-emerald-800 ring-emerald-200";
        label = `Trusted timestamp · ${rfc3161.signed_at_per_tsa ? rfc3161.signed_at_per_tsa.slice(0, 19).replace("T", " ") : "valid"}`;
    } else if (status === "ok" && valid === false) {
        tone = "bg-red-50 text-red-800 ring-red-200";
        label = "Trusted timestamp: invalid";
    } else if (status === "failed") {
        tone = "bg-amber-50 text-amber-900 ring-amber-200";
        label = "Trusted timestamp: retry needed";
    }
    return (
        <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-semibold ring-1 ${tone}`}
            data-testid="contract-timestamp-pill">
            <ShieldCheck size={12} />
            <span data-testid="contract-timestamp-pill-label">{label}</span>
            {status === "ok" && (
                <button type="button" onClick={onVerify} disabled={verifying}
                    className="underline decoration-dotted hover:decoration-solid text-[10px]"
                    data-testid="contract-timestamp-verify">
                    {verifying ? "verifying…" : "verify"}
                </button>
            )}
            {(status === "failed" || status === "none") && (
                <button type="button" onClick={onRetry} disabled={retrying}
                    className="underline decoration-dotted hover:decoration-solid text-[10px]"
                    data-testid="contract-timestamp-retry">
                    {retrying ? "requesting…" : "retry"}
                </button>
            )}
        </span>
    );
}


// Bug 4 — Send-for-signature review snapshot. Renders the read-only
// rental + contract-setup summary plus a warnings callout when any of
// the seven derived flags fire. Pure / stateless — driven entirely by
// props pulled from the parent's already-loaded contract + rental +
// customer state.
const EQUIPMENT_LABELS = {
    reefer: "Reefer",
    dry_van: "Dry Van",
    conestoga: "Conestoga",
    flatbed: "Flatbed",
    step_deck: "Step Deck",
    other: "Other",
};

function _money(v) {
    if (v === null || v === undefined || v === "") return "—";
    const n = Number(v);
    if (Number.isNaN(n)) return String(v);
    return n.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 });
}

function SendConfirmSnapshot({
    contract, rental, customer, templates, achTemplates, coiTemplates,
    templateId, achTemplateId, coiTemplateId, onFixSetup, onFixRental,
}) {
    const equipmentLine = rental
        ? `${EQUIPMENT_LABELS[rental.equipment_type] || rental.equipment_type || "—"}${rental.unit_number ? ` · unit ${rental.unit_number}` : ""}`
        : "—";
    const makeModelYear = rental
        ? [rental.make || "—", rental.model || "—", rental.year || "—"].join(" / ")
        : "—";
    const startDate = rental?.start_date || "—";
    const endDate = rental?.end_date || "— (open)";
    const billing = rental
        ? (rental.billing_period === "monthly"
            ? `Monthly · ${_money(rental.monthly_rate)}`
            : rental.billing_period === "daily"
                ? `Daily · ${_money(rental.daily_rate)}`
                : "—")
        : "—";
    const minTerm = rental?.minimum_term_months ?? null;
    const deposit = rental?.deposit;
    let depositVal = null;
    if (deposit !== null && deposit !== undefined && deposit !== "") {
        // Phase DP polish — deposit can be a dict {amount_cents,...},
        // a float ($), or a string. Normalise for display only.
        if (typeof deposit === "object" && deposit.amount_cents != null) {
            depositVal = deposit.amount_cents / 100;
        } else {
            depositVal = Number(deposit);
        }
    }
    const customerCompany = customer?.company_name || "—";
    const customerIdShort = (contract?.customer_id || "").slice(0, 8);
    const tplName = templateId
        ? (templates.find((t) => t.id === templateId)?.name || "(unknown template)")
        : "(no template — fallback layout)";
    const achTplName = achTemplateId
        ? (achTemplates.find((t) => t.id === achTemplateId)?.name || "(unknown ACH template)")
        : "None";
    const coiTplName = coiTemplateId
        ? (coiTemplates.find((t) => t.id === coiTemplateId)?.name || "(unknown COI template)")
        : "None";

    // Derive client-side warnings. Order matches the spec; each warning
    // carries a ``fix`` action ("setup" → scroll to settings, "rental"
    // → open the Edit rental modal). Warnings render in the amber
    // callout at the bottom of the snapshot.
    const warnings = [];
    if (!templateId) warnings.push({ key: "no_tpl", text: "No contract template selected — placeholder layout will be sent.", fix: "setup" });
    if (depositVal === null || depositVal === 0) warnings.push({ key: "no_deposit", text: "No deposit set on this rental.", fix: "rental" });
    if (!rental?.end_date) warnings.push({ key: "open_ended", text: "No end date — open-ended rental.", fix: "rental" });
    if (!achTemplateId) warnings.push({ key: "no_ach", text: "No ACH template attached.", fix: "setup" });
    if (contract?.type === "rental_agreement" && !coiTemplateId) warnings.push({ key: "no_coi", text: "No COI requirement attached.", fix: "setup" });
    const hasGuarantor = !!(
        customer?.guarantor_name
        || customer?.guarantor_email
        || customer?.personal_guarantor
        || customer?.guarantor
    );
    if (!hasGuarantor) warnings.push({ key: "no_guarantor", text: "No guarantor info on file.", fix: "rental" });
    const customerEmail = customer?.billing_contact_email || customer?.primary_contact_email || customer?.customer_email;
    if (!customerEmail) warnings.push({ key: "no_email", text: "Customer email missing or unverified.", fix: "rental" });

    return (
        <div className="space-y-4" data-testid="contract-send-confirm-snapshot">
            <section
                className="rounded-lg border border-slate-200 bg-slate-50/60 px-4 py-3 space-y-2"
                data-testid="contract-send-confirm-rental-snapshot"
            >
                <p className="text-[11px] font-medium tracking-[0.18em] uppercase text-slate-500">Rental snapshot</p>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
                    <Row label="Equipment" value={equipmentLine} />
                    <Row label="Make / Model / Year" value={makeModelYear} />
                    <Row label="VIN" value={rental?.vin || "—"} />
                    <Row label="Plate" value={rental?.plate || "—"} />
                    <Row label="Customer" value={
                        <span>
                            {customerCompany}
                            {customerIdShort && <span className="text-slate-500"> · {customerIdShort}</span>}
                        </span>
                    } />
                    <Row label="Billing" value={billing} />
                    <Row label="Start date" value={startDate} />
                    <Row label="End date" value={endDate} />
                    <Row label="Min term" value={minTerm ? `${minTerm} months` : "—"} />
                    <Row label="Deposit" value={depositVal === null ? "—" : _money(depositVal)} />
                </dl>
            </section>

            <section
                className="rounded-lg border border-slate-200 bg-white px-4 py-3 space-y-2"
                data-testid="contract-send-confirm-setup-snapshot"
            >
                <p className="text-[11px] font-medium tracking-[0.18em] uppercase text-slate-500">Contract setup</p>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
                    <Row label="Contract Template" value={tplName} />
                    <Row label="ACH Template" value={achTplName} />
                    {contract?.type === "rental_agreement" && (
                        <Row label="COI Requirements" value={coiTplName} />
                    )}
                </dl>
            </section>

            {warnings.length > 0 && (
                <section
                    className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3"
                    data-testid="contract-send-confirm-warnings"
                >
                    <p className="text-[11px] font-medium tracking-[0.18em] uppercase text-amber-900 flex items-center gap-1.5">
                        <AlertTriangle size={12} /> Heads up — {warnings.length} item{warnings.length === 1 ? "" : "s"} to review
                    </p>
                    <ul className="mt-2 space-y-1.5 text-sm text-amber-900">
                        {warnings.map((w) => (
                            <li
                                key={w.key}
                                className="flex items-start gap-2"
                                data-testid={`contract-send-confirm-warning-${w.key}`}
                            >
                                <span className="text-amber-700 mt-0.5">⚠</span>
                                <span className="flex-1">{w.text}</span>
                                <button
                                    type="button"
                                    onClick={() => (w.fix === "setup" ? onFixSetup() : onFixRental())}
                                    className="underline decoration-dotted hover:decoration-solid text-amber-900 text-xs font-medium shrink-0"
                                    data-testid={`contract-send-confirm-warning-${w.key}-fix`}
                                >
                                    Fix
                                </button>
                            </li>
                        ))}
                    </ul>
                </section>
            )}
        </div>
    );
}

function Row({ label, value }) {
    return (
        <div className="flex items-baseline gap-2 min-w-0">
            <dt className="text-xs text-slate-500 shrink-0 min-w-[6.5rem]">{label}</dt>
            <dd className="text-sm text-slate-900 truncate" title={typeof value === "string" ? value : undefined}>{value}</dd>
        </div>
    );
}

